if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-recommendations-list]');
      this.productIds = (this.dataset.productIds || '')
        .split(',')
        .filter(Boolean)
        .map((id) => id.trim());
      this.currency = this.dataset.currency;
      this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      if (this.productIds.length) {
        this.loadRecommendations();
      }
    }

    async loadRecommendations() {
      try {
        const resultsPerProduct = await Promise.all(
          this.productIds.map((id) => this.fetchRecommendationsForProduct(id))
        );

        let products = this.dedupe(resultsPerProduct.flat());
        products = products
          .filter((p) => !this.productIds.includes(String(p.id)))
          .slice(0, 4);

        if (products.length) {
          this.render(products);
        } else {
          this.hidden = true;
        }
      } catch (error) {
        console.error('cart-recommendations: failed to load', error);
        this.hidden = true;
      }
    }

    async fetchRecommendationsForProduct(productId) {
      let products = await this.fetchRecommendations(productId, 'complementary');
      if (!products.length) {
        products = await this.fetchRecommendations(productId, 'related');
      }
      return products;
    }

    async fetchRecommendations(productId, intent) {
      const url = `${this.root}recommendations/products.json?product_id=${productId}&limit=6&intent=${intent}`;
      const response = await fetch(url);
      if (!response.ok) return [];
      const data = await response.json();
      return data.products || [];
    }

    dedupe(products) {
      const seen = new Set();
      const result = [];
      for (const product of products) {
        if (!seen.has(product.id)) {
          seen.add(product.id);
          result.push(product);
        }
      }
      return result;
    }

    render(products) {
      this.list.innerHTML = products
        .map((product) => {
          const variant = product.variants[0];
          const image = product.featured_image || (product.images && product.images[0]) || '';
          const price = this.formatMoney(variant.price);
          return `
            <div class="cart-recommendation-card">
              <img src="${image}" alt="${this.escape(product.title)}" loading="lazy" width="110" height="110">
              <p class="cart-recommendation-card__title">${this.escape(product.title)}</p>
              <p class="cart-recommendation-card__price">${price}</p>
              <button type="button" data-variant-id="${variant.id}">Add</button>
            </div>
          `;
        })
        .join('');

      this.list.hidden = false;

      this.list.querySelectorAll('button[data-variant-id]').forEach((button) => {
        button.addEventListener('click', (event) => this.addToCart(event.currentTarget));
      });
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        await this.refreshCartDrawer();
      } catch (error) {
        console.error('cart-recommendations: add to cart failed', error);
        button.disabled = false;
        button.textContent = 'Add';
      }
    }

    async refreshCartDrawer() {
      const response = await fetch(`${this.root}?section_id=cart-drawer-section`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.getElementById('shopify-section-cart-drawer-section');
      const currentSection = document.getElementById('shopify-section-cart-drawer-section');

      if (newSection && currentSection) {
        currentSection.innerHTML = newSection.innerHTML;
      }
    }

    formatMoney(priceString) {
      const amount = parseFloat(priceString) / 100;
      if (Number.isNaN(amount)) return priceString;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency || 'USD',
        }).format(amount);
      } catch (e) {
        return `$${amount.toFixed(2)}`;
      }
    }

    escape(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
}